import { useEffect } from 'react'

export interface KeyboardShortcut {
  key: string
  meta?: boolean   // Cmd on Mac, Windows key on PC
  ctrl?: boolean
  shift?: boolean
  handler: () => void
  description: string  // shown in shortcut cheat sheet
}

/**
 * Registers keyboard shortcuts at the document level.
 * Automatically cleans up listeners on unmount.
 *
 * Matches: key + modifier combo. Non-matching events are ignored.
 * Does NOT fire when focus is inside an input, textarea, or contenteditable
 * (prevents conflicts with user typing).
 *
 * @example
 * useKeyboardShortcuts([
 *   { key: 'k', meta: true, handler: openSearch, description: 'Search' },
 *   { key: 'p', meta: true, handler: openPacer, description: 'Open Pacer' },
 *   { key: '?',             handler: showHelp,   description: 'Shortcuts' },
 * ])
 */
export function useKeyboardShortcuts(shortcuts: KeyboardShortcut[]) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Skip when user is typing in a form field
      const target = e.target as HTMLElement
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
      ) {
        return
      }

      for (const shortcut of shortcuts) {
        const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase()
        const metaMatch = (shortcut.meta ?? false) === e.metaKey
        const ctrlMatch = (shortcut.ctrl ?? false) === e.ctrlKey
        const shiftMatch = (shortcut.shift ?? false) === e.shiftKey

        if (keyMatch && metaMatch && ctrlMatch && shiftMatch) {
          e.preventDefault()
          shortcut.handler()
          return
        }
      }
    }

    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [shortcuts])
}
